import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Badge from '@atlaskit/badge/badge';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { SuggestionCard } from './SuggestionCard.tsx';
import type { SuggestionItem } from './SuggestionCard.tsx';
import { SuggestionReviewPanel } from './review-panel/SuggestionReviewPanel.tsx';

type NeedsReviewSectionProps = {
	items: SuggestionItem[];
	onReview: (item: SuggestionItem) => void;
};

export function NeedsReviewSection({ items, onReview }: NeedsReviewSectionProps) {
	const { formatMessage } = useIntl();
	const [selectedItem, setSelectedItem] = useState<SuggestionItem | null>(null);

	const handleReview = useCallback(
		(item: SuggestionItem) => {
			setSelectedItem(item);
			onReview(item);
		},
		[onReview],
	);

	const handleClosePanel = useCallback(() => {
		setSelectedItem(null);
	}, []);

	return (
		<>
			{selectedItem !== null && (
				<SuggestionReviewPanel
					actions={selectedItem.actions ?? []}
					timestamp={selectedItem.timestamp}
					onClose={handleClosePanel}
					onCancel={handleClosePanel}
					onApprove={handleClosePanel}
				>
					{null}
				</SuggestionReviewPanel>
			)}
			<Stack space="space.100">
				<Box xcss={styles.title}>
					<Inline alignBlock="center" space="space.100">
						<Heading size="small">{formatMessage(i18n.needsReviewTitle)}</Heading>
						<Badge>{items.length}</Badge>
					</Inline>
				</Box>
				{items.map((item, index) => (
					// TODO: use a stable unique id from item once real data is available
					// eslint-disable-next-line react/no-array-index-key
					<SuggestionCard
						key={index}
						item={item}
						onReview={handleReview}
						isSelected={selectedItem === item}
					/>
				))}
			</Stack>
		</>
	);
}

const styles = cssMap({
	title: {
		marginBottom: token('space.050'),
	},
});

const i18n = defineMessages({
	needsReviewTitle: {
		id: 'space-suggestions.needs-review.section-title',
		defaultMessage: 'Needs review',
		description:
			'Title for the "needs review" section. The number of suggestions awaiting review is shown next to it in a badge.',
	},
});
